/**
 * @author mschuetz / http://mschuetz.at
 *
 *
 */

import * as THREE from 'three';
import { EventDispatcher } from '../EventDispatcher';
import { KeyCodes } from '../KeyCodes';
import { Utils } from '../utils';
import { MeasureTools, type Measure } from '../utils/Measure';
import { isAnnotationPickBlocked, type AnnotationCategory } from '../viewer/AnnotationBlacklistStack';
import { isDoorWindowAnnotation } from '../utils/PointoramaAnnotation';

/** Loose viewer interface for InputHandler - subset of the Potree viewer */
interface InputHandlerViewer {
  renderer: THREE.WebGLRenderer;
  scene: {
    view: { clone(): unknown; radius: number; direction: THREE.Vector3; position: THREE.Vector3 };
    getActiveCamera(): THREE.Camera;
    pointclouds: THREE.Object3D[];
    measurements: Array<{
      visible: boolean;
      isCADMeasure: boolean;
      type: string;
      identifier?: string;
      groupIdentifier?: string | null;
      spheres: Array<{ mesh: THREE.Object3D } & THREE.Object3D>;
      backgroundLine: THREE.Object3D;
      backgroundLineTouch: THREE.Object3D;
    }>;
    planes: Array<{ visible: boolean; finished: boolean; fullCircle: THREE.Object3D }>;
    surfaces: Array<{ visible: boolean; finished: boolean; getSelectableMesh(): THREE.Object3D }>;
    volumes: Array<THREE.Object3D & { visible: boolean; finished: boolean; getVolume(): number }>;
    rooms: Array<{
      visible: boolean;
      identifier?: string;
      groupIdentifier?: string | null;
      spheres?: Array<{ mesh: THREE.Object3D } & THREE.Object3D>;
      backgroundLine: THREE.Object3D;
      backgroundLineTouch: THREE.Object3D;
    }>;
    /** Needed by isAnnotationPickBlocked's legacy-outline tombstone check. */
    wallSketches: Array<{ groupIdentifier: string | null; getOutlineEdges(): unknown[] }>;
  };
  renderViews: Array<{ viewState?: { getAnnotationBlacklist(): import('../viewer/AnnotationBlacklistStack').AnnotationBlacklist | null } | null }>;
  inputHandler: InputHandler;
  selectionTool: { active: boolean; onClick(ctrl: boolean): void; onTap(): void; onMouseMove(): void };
  floorplanTool: {
    active: boolean;
    onClick(mouse: THREE.Vector2, button: number): void;
    onMouseMove(mouse: THREE.Vector2): void;
    onDoubleClick(): void;
    onCtrlDown(): void;
    onCtrlUp(): void;
    onShiftDown(): void;
    onShiftUp(): void;
    onAltDown(): void;
    onAltUp(): void;
    getTemporaryWallPlanes(): THREE.Object3D[];
  };
  viewerMode: {
    in2DMode: boolean;
    associatedPlane: { getNormal(): THREE.Vector3; position: THREE.Vector3 } | null;
  };
  personaManager?: {
    isPersonaActive(id: string): boolean;
    /** Needed by isAnnotationPickBlocked's floorplan-mode sketch gate. */
    getActivePersona(): { name: string; getCurrentMode(): { id: string } | undefined } | undefined;
  };
  getMoveSpeed(): number;
  setMoveSpeed(speed: number): void;
}

interface InputListenerEntry extends EventDispatcher {
  importance?: number;
}

interface DragState {
  start: THREE.Vector2;
  end: THREE.Vector2;
  lastDrag: THREE.Vector2;
  startView: unknown;
  dragTarget: unknown;
  mouse?: number;
  event?: MouseEvent;
}

interface HoveredElement {
  object: EventDispatcher;
}

interface GetHoveredRoomOptions {
  excludedRooms?: unknown[];
}

/**
 * Mouse back/forward (buttons 3/4). Left alone so the browser can navigate
 * history — preventDefault on their mousedown/mouseup swallows that.
 */
const isHistoryButton = (e: MouseEvent) => e.button === 3 || e.button === 4;

export class InputHandler extends EventDispatcher {
  viewer: InputHandlerViewer;
  renderer: THREE.WebGLRenderer;
  domElement: HTMLCanvasElement;
  enabled: boolean;
  scene: InputHandlerViewer['scene'] | null;
  interactiveScenes: unknown[];
  inputListeners: InputListenerEntry[];
  blacklist: Set<unknown>;
  drag: DragState | null;
  mouse: THREE.Vector2;
  nmouse: THREE.Vector2;
  raycaster: THREE.Raycaster;
  pressedKeys: Record<number, boolean>;
  wheelDelta: number;
  speed: number;
  logMessages: boolean;
  selectionDisabled: boolean;
  _orbitBlocked: boolean;
  lastClick: number;
  lastTouchEnd: number;
  lastTouchStart: number;
  touchMoved: boolean;
  mouseDown: boolean;
  hoveredElement: HoveredElement | null;
  viewStart: unknown;
  ignoreNextTouchEnd: boolean;
  mouseHasMovedSinceMouseDown: boolean;
  mouseHasMovedSinceLastClick: boolean;

  constructor(viewer: InputHandlerViewer) {
    super();

    this.viewer = viewer;
    this.renderer = viewer.renderer;
    this.domElement = this.renderer.domElement;
    this.enabled = true;

    this.scene = null;
    this.interactiveScenes = [];
    this.inputListeners = [];
    this.blacklist = new Set();

    this.drag = null;
    this.mouse = new THREE.Vector2(0, 0);
    this.nmouse = new THREE.Vector2(0, 0);
    this.raycaster = new THREE.Raycaster();

    this.pressedKeys = {};

    this.wheelDelta = 0;

    this.speed = 1;

    this.logMessages = false;
    // Pythagoras begin
    this.selectionDisabled = false; // Tools can set this to prevent selection/deselection on click
    this._orbitBlocked = false; // Managed via blockOrbitControls / unblockOrbitControls
    this.lastClick = 0;
    this.lastTouchEnd = 0;
    this.lastTouchStart = 0;
    this.touchMoved = false;
    this.mouseDown = false;
    // Pythagoras end
    this.hoveredElement = null;
    this.viewStart = null;
    this.ignoreNextTouchEnd = false;
    this.mouseHasMovedSinceMouseDown = false;
    this.mouseHasMovedSinceLastClick = false;

    if (this.domElement.tabIndex === -1) {
      this.domElement.tabIndex = 2222;
    }

    this.domElement.addEventListener(
      'contextmenu',
      (event) => {
        event.preventDefault();
      },
      false,
    );
    this.domElement.addEventListener('click', this.onMouseClick.bind(this), false);
    this.domElement.addEventListener('mousedown', this.onMouseDown.bind(this), false);
    this.domElement.addEventListener('mouseup', this.onMouseUp.bind(this), false);
    this.domElement.addEventListener('mousemove', this.onMouseMove.bind(this), false);
    this.domElement.addEventListener('wheel', this.onMouseWheel.bind(this), { passive: false });
    window.addEventListener('keydown', this.onKeyDown.bind(this));
    window.addEventListener('keyup', this.onKeyUp.bind(this));
    this.domElement.addEventListener('touchstart', this.onTouchStart.bind(this));
    this.domElement.addEventListener('touchend', this.onTouchEnd.bind(this));
    this.domElement.addEventListener('touchmove', this.onTouchMove.bind(this));
  }

  addInputListener(listener: InputListenerEntry) {
    this.inputListeners.push(listener);
  }

  removeInputListener(listener: InputListenerEntry) {
    this.inputListeners = this.inputListeners.filter((e) => e !== listener);
  }

  getSortedListeners() {
    return this.inputListeners.sort((a, b) => {
      const ia = a.importance !== undefined ? a.importance : 0;
      const ib = b.importance !== undefined ? b.importance : 0;

      return ib - ia;
    });
  }

  /** Block orbit/pan controls from receiving drag events. Call unblockOrbitControls() to re-enable. */
  blockOrbitControls() {
    this._orbitBlocked = true;
  }

  /** Re-enable orbit/pan controls after a blockOrbitControls() call. */
  unblockOrbitControls() {
    this._orbitBlocked = false;
  }

  onTouchStart(e: TouchEvent) {
    if (this.logMessages) console.error(this.constructor.name + ': onTouchStart');
    e.preventDefault();
    const currentTime = new Date().getTime();
    const tapInterval = currentTime - this.lastTouchStart;
    this.lastTouchStart = currentTime;
    this.touchMoved = false;

    // Pan, orbit
    if (e.touches.length > 1) {
      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: e.type,
          touches: e.touches,
          changedTouches: e.changedTouches,
        });
      }
      return;
    }

    const rect = this.domElement.getBoundingClientRect();
    const x = e.touches[0].pageX - rect.left;
    const y = e.touches[0].pageY - rect.top;
    this.setMouse(x, y);

    // Double tap
    if (tapInterval < 200 && tapInterval > 0) {
      this.onDoubleClick();
      this.ignoreNextTouchEnd = true;
      return;
    }

    // Tap or tap and hold screen
    this.startDragging();
  }

  onTouchEnd(e: TouchEvent) {
    e.preventDefault();
    if (this.logMessages) console.error(this.constructor.name + ': onTouchEnd');
    this.lastTouchEnd = new Date().getTime();

    const handleRegularTouchEnd = () => {
      e.preventDefault();

      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'drop',
          drag: this.drag,
          viewer: this.viewer,
        });
      }

      this.drag = null;
      this.touchMoved = false;

      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: e.type,
          touches: e.touches,
          changedTouches: e.changedTouches,
        });
      }
    };

    const rect = this.domElement.getBoundingClientRect();
    const x = e.changedTouches[0].pageX - rect.left;
    const y = e.changedTouches[0].pageY - rect.top;
    this.setMouse(x, y);

    // Multiple fingers, touchEnd already triggered
    if (this.ignoreNextTouchEnd) {
      if (e.touches.length === 0) {
        this.ignoreNextTouchEnd = false;
      }
      return;
    }
    // Multiple fingers, first touchEnd
    if (e.touches.length > 0) {
      this.ignoreNextTouchEnd = true;
      // Pan or Zoom
      if (this.touchMoved) {
        handleRegularTouchEnd();
        return;
      }
      // Two finger tap
      if (e.touches.length === 1) {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
        if (this.viewer.floorplanTool.active) {
          this.viewer.floorplanTool.onClick(this.mouse, THREE.MOUSE.RIGHT);
        }
        return;
      }
      // More than two finger tap
      if (e.touches.length > 1) {
        return;
      }
    }
    // Single finger drag
    if (this.drag && (this.drag.lastDrag.x !== 0 || this.drag.lastDrag.y !== 0)) {
      handleRegularTouchEnd();
      return;
    }

    // Single finger tap
    // Skip selection tool when surface persona is active (surface tools handle their own selection)
    // Also skip when selectionDisabled flag is set (tools can disable selection temporarily)
    const isSurfacePersonaForTap = this.viewer.personaManager?.isPersonaActive('surface');
    if (this.viewer.selectionTool.active && !isSurfacePersonaForTap && !this.selectionDisabled) {
      this.viewer.selectionTool.onTap();
    } else if (this.viewer.floorplanTool.active) {
      this.viewer.floorplanTool.onClick(this.mouse, THREE.MOUSE.LEFT);
    }
  }

  onTouchMove(e: TouchEvent) {
    const currentTime = new Date().getTime();
    if (currentTime - this.lastTouchStart < 100) return;
    this.touchMoved = true;

    e.preventDefault();

    if (e.touches.length > 1) {
      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: e.type,
          touches: e.touches,
          changedTouches: e.changedTouches,
        });
      }
      return;
    }

    const rect = this.domElement.getBoundingClientRect();
    const x = e.touches[0].pageX - rect.left;
    const y = e.touches[0].pageY - rect.top;
    this.setMouse(x, y);

    if (this.drag) {
      this.drag.mouse = 1;

      this.drag.lastDrag.x = x - this.drag.end.x;
      this.drag.lastDrag.y = y - this.drag.end.y;

      this.drag.end.set(x, y);

      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'drag',
          drag: this.drag,
          viewer: this.viewer,
        });
      }
    }
  }

  onKeyDown(e: KeyboardEvent) {
    if (this.logMessages) console.error(this.constructor.name + ': onKeyDown');

    if (this.pressedKeys[e.keyCode]) return;

    // Handle keyboard events for floorplan tool
    if (this.viewer.floorplanTool.active) {
      if (e.keyCode === KeyCodes.CONTROL) {
        this.viewer.floorplanTool.onCtrlDown();
      } else if (e.keyCode === KeyCodes.SHIFT) {
        this.viewer.floorplanTool.onShiftDown();
      } else if (e.keyCode === KeyCodes.CAPSLOCK) {
        // CapsLock acts as a toggle - check state AFTER key press
        if (e.getModifierState('CapsLock')) {
          this.viewer.floorplanTool.onShiftDown();
        } else {
          this.viewer.floorplanTool.onShiftUp();
        }
      } else if (e.keyCode === KeyCodes.ALT) {
        this.viewer.floorplanTool.onAltDown();
      }
      this.viewer.floorplanTool.onMouseMove(this.mouse);
    }

    // Legacy keyboard routing removed - persona tools now handle their own keyboard input
    // via BaseTool.onKeyDown handlers

    this.dispatchEvent({
      type: 'keydown',
      keyCode: e.keyCode,
      event: e,
    });

    this.pressedKeys[e.keyCode] = true;
  }

  onKeyUp(e: KeyboardEvent) {
    if (this.logMessages) console.error(this.constructor.name + ': onKeyUp');

    // Handle keyboard events for floorplan tool
    if (this.viewer.floorplanTool.active) {
      if (e.keyCode === KeyCodes.CONTROL) {
        this.viewer.floorplanTool.onCtrlUp();
      } else if (e.keyCode === KeyCodes.SHIFT) {
        this.viewer.floorplanTool.onShiftUp();
      } else if (e.keyCode === KeyCodes.ALT) {
        this.viewer.floorplanTool.onAltUp();
      }
      // Note: CapsLock is handled in onKeyDown only, as it's a toggle key
      this.viewer.floorplanTool.onMouseMove(this.mouse);
    }

    // Legacy keyboard routing removed - persona tools now handle their own keyboard input
    // via BaseTool.onKeyUp handlers

    delete this.pressedKeys[e.keyCode];

    e.preventDefault();
  }

  onDoubleClick() {
    if (this.logMessages) console.error(this.constructor.name + ': onDoubleClick');

    if (this.viewer.floorplanTool.active) {
      this.viewer.floorplanTool.onDoubleClick();
      return;
    }

    for (const inputListener of this.getSortedListeners()) {
      inputListener.dispatchEvent({
        type: 'dblclick',
        mouse: this.mouse,
        object: null,
      });
    }
  }

  onMouseClick(e: MouseEvent) {
    if (this.logMessages) console.error(this.constructor.name + ': onMouseClick');

    e.preventDefault();
  }

  onMouseDown(e: MouseEvent) {
    if (this.logMessages) console.error(this.constructor.name + ': onMouseDown');
    if (isHistoryButton(e)) return;
    e.preventDefault();
    // workaround when ctrl was held while browser window was not active, needed for lasso tool
    if (!this.pressedKeys[KeyCodes.CONTROL] && e.ctrlKey) {
      this.onKeyDown({ ...e, keyCode: KeyCodes.CONTROL } as unknown as KeyboardEvent);
    }

    this.mouseDown = true;
    this.mouseHasMovedSinceMouseDown = false;

    for (const inputListener of this.getSortedListeners()) {
      inputListener.dispatchEvent({
        type: 'mousedown',
        viewer: this.viewer,
        mouse: this.mouse,
      });
    }

    if (!this.drag) {
      this.startDragging();
    }

    if (this.scene) {
      this.viewStart = this.scene.view.clone();
    }
  }

  onMouseUp(e: MouseEvent) {
    if (this.logMessages) console.error(this.constructor.name + ': onMouseUp');
    if (isHistoryButton(e)) return;

    e.preventDefault();

    this.mouseDown = false;
    if (this.mouseHasMovedSinceMouseDown) {
      this.mouseHasMovedSinceMouseDown = false;
    } else {
      const interval = new Date().getTime() - this.lastClick;
      if (interval < 300 && !this.mouseHasMovedSinceLastClick && e.button === THREE.MOUSE.LEFT) {
        // Double click
        this.onDoubleClick();
      } else {
        // Click
        this.lastClick = new Date().getTime();
        this.mouseHasMovedSinceLastClick = false;

        // Skip selection tool when surface persona is active (surface tools handle their own selection)
        // Also skip when selectionDisabled flag is set (tools can disable selection temporarily)
        const isSurfacePersonaForSelection = this.viewer.personaManager?.isPersonaActive('surface');
        if (this.viewer.selectionTool.active && e.button === THREE.MOUSE.LEFT && !isSurfacePersonaForSelection && !this.selectionDisabled) {
          // Prefer the event's own modifier flags (they include Cmd on macOS and
          // never go stale); pressedKeys stays as fallback for synthetic events.
          // Shift counts as the additive modifier too — a shift-click that hits
          // nothing must not clear an accumulating multi-selection.
          this.viewer.selectionTool.onClick(e.ctrlKey || e.metaKey || e.shiftKey || this.pressedKeys[KeyCodes.CONTROL]);
        } else if (this.viewer.floorplanTool.active) {
          this.viewer.floorplanTool.onClick(this.mouse, e.button);
        }
      }
    }

    if (this.drag) {
      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'drop',
          drag: this.drag,
          viewer: this.viewer,
        });
      }

      this.drag = null;
    }
  }

  onMouseMove(e: MouseEvent) {
    e.preventDefault();
    this.mouseHasMovedSinceMouseDown = true;
    this.mouseHasMovedSinceLastClick = true;
    const rect = this.domElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    this.setMouse(x, y);

    if (this.viewer.selectionTool.active && !this.selectionDisabled) {
      this.viewer.selectionTool.onMouseMove();
    } else if (this.viewer.floorplanTool.active) {
      this.viewer.floorplanTool.onMouseMove(this.mouse);
    }

    // A pure middle-button drag stays a camera pan even while a tool blocks
    // orbit controls — tools reserve middle for the camera (e.g. panning
    // between the two clicks of click-click wall drawing). Mixed-button
    // states (tool gesture + middle pressed mid-drag) stay blocked.
    if (this.drag && this._orbitBlocked && e.buttons !== 4) {
      return;
    }

    if (this.drag) {
      this.drag.mouse = e.buttons;
      this.drag.event = e; // Store the mouse event for accessing modifier keys

      this.drag.lastDrag.x = x - this.drag.end.x;
      this.drag.lastDrag.y = y - this.drag.end.y;

      this.drag.end.set(x, y);

      let dragConsumed = false;
      const consumeDrag = () => {
        dragConsumed = true;
      };
      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'drag',
          drag: this.drag,
          viewer: this.viewer,
          consume: consumeDrag,
        });

        if (dragConsumed) {
          break;
        }
      }

      return;
    }
  }

  onMouseWheel(e: WheelEvent) {
    if (!this.enabled) return;

    e.preventDefault();

    // Standard wheel event: deltaY is positive when scrolling down
    const ndelta = -Math.sign(e.deltaY);

    // Get horizontal scroll delta (for side scroll wheel) and normalize it
    let ndeltaX = 0;
    if (e.deltaX !== undefined && e.deltaX !== 0) {
      ndeltaX = Math.sign(e.deltaX);
    }

    if (this.hoveredElement) {
      this.hoveredElement.object.dispatchEvent({
        type: 'mousewheel',
        delta: ndelta,
        deltaX: ndeltaX,
        event: e,
        mouse: this.mouse.clone(),
        object: this.hoveredElement.object,
      });
    } else {
      for (const inputListener of this.getSortedListeners()) {
        inputListener.dispatchEvent({
          type: 'mousewheel',
          delta: ndelta,
          deltaX: ndeltaX,
          event: e,
          mouse: this.mouse.clone(),
          object: null,
        });
      }
    }
  }

  startDragging() {
    this.drag = {
      start: this.mouse.clone(),
      end: this.mouse.clone(),
      lastDrag: new THREE.Vector2(0, 0),
      startView: this.scene ? this.scene.view.clone() : null,
      dragTarget: null,
    };
  }

  getMousePointCloudIntersection(_mouse: THREE.Vector2) {
    if (!this.scene) return null;
    return Utils.getMousePointCloudIntersection(
      this.mouse,
      this.scene.getActiveCamera(),
      this.viewer as unknown as Parameters<typeof Utils.getMousePointCloudIntersection>[2],
      this.scene.pointclouds as unknown as Parameters<typeof Utils.getMousePointCloudIntersection>[3],
      { pickClipped: false },
    );
  }

  registerInteractiveScene(scene: unknown) {
    const index = this.interactiveScenes.indexOf(scene);
    if (index === -1) {
      this.interactiveScenes.push(scene);
    }
  }

  unregisterInteractiveScene(scene: unknown) {
    const index = this.interactiveScenes.indexOf(scene);
    if (index > -1) {
      this.interactiveScenes.splice(index, 1);
    }
  }

  getIntersectWith2DPlane() {
    if (!this.viewer.viewerMode.in2DMode) return null;

    const ray = this.raycaster.ray;

    const associatedPlane2DMode = this.viewer.viewerMode.associatedPlane;

    let raycastingPlane;
    if (associatedPlane2DMode) {
      raycastingPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(associatedPlane2DMode.getNormal(), associatedPlane2DMode.position.clone());
    } else {
      const origin = ray.origin.clone();
      origin.z = 0;
      raycastingPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(new THREE.Vector3(0, 0, 1), origin);
    }

    const intersectionPoint = ray.intersectPlane(raycastingPlane, new THREE.Vector3());

    return intersectionPoint;
  }

  getIntersectWithPlane(plane: { getNormal(): THREE.Vector3; position: THREE.Vector3 }) {
    const ray = this.raycaster.ray;

    const raycastingPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(plane.getNormal(), plane.position.clone());

    const intersectionPoint = ray.intersectPlane(raycastingPlane, new THREE.Vector3());

    return intersectionPoint;
  }

  /**
   * True when the active view's annotation blacklist would hide this object —
   * so it must not be pickable either. The render blacklist only flips
   * `.visible` during a frame, so picking (between frames) needs this explicit
   * check. Used to keep persona-hidden annotations (e.g. rooms/outlines/openings
   * in the Layout persona) non-selectable.
   */
  private isMeasurePickBlocked(m: InputHandlerViewer['scene']['measurements'][number]): boolean {
    const category: AnnotationCategory = isDoorWindowAnnotation(m as unknown as Measure) ? 'OPENING' : (m.type as AnnotationCategory);
    return isAnnotationPickBlocked(this.viewer, { visible: m.visible, identifier: m.identifier, groupIdentifier: m.groupIdentifier }, category);
  }

  getHoveredMeasure() {
    const hoverables = this.viewer.scene.measurements
      .filter((m) => m.visible)
      .filter((m) => !this.isMeasurePickBlocked(m))
      .filter((m) => !m.isCADMeasure)
      .flatMap((m) => {
        const measureHoverables: THREE.Object3D[] = [...m.spheres];
        if (m.type !== 'POINT') {
          measureHoverables.push(m.backgroundLine);
        }
        return measureHoverables;
      });

    const intersects = this.raycaster.intersectObjects(hoverables);

    return this.getParentMeasure(intersects.at(0)?.object);
  }

  getHoveredPlane() {
    const planes = [...this.viewer.scene.planes.filter((m) => m.visible && m.finished).map((m) => m.fullCircle), ...this.viewer.floorplanTool.getTemporaryWallPlanes()];
    const intersects = this.raycaster.intersectObjects(planes);
    return intersects.at(0)?.object.parent;
  }

  getHoveredSurface() {
    const surfaces = this.viewer.scene.surfaces.filter((s) => s.visible && s.finished).map((s) => s.getSelectableMesh());
    const intersects = this.raycaster.intersectObjects(surfaces);
    return intersects.at(0)?.object.parent;
  }

  /**
   * Get the room that is currently hovered by the mouse
   * Uses the room's background line for detection (wider hit area)
   */
  getHoveredRoom({ excludedRooms }: GetHoveredRoomOptions = {}) {
    const roomLines = this.viewer.scene.rooms
      .filter((r) => r.visible)
      .filter((r) => (excludedRooms ? !excludedRooms.includes(r) : true))
      .map((r) => r.backgroundLine);

    const intersects = this.raycaster.intersectObjects(roomLines);
    const intersect = intersects.at(0);
    if (!intersect) return null;

    // Find the parent room from the background line
    let parent: (THREE.Object3D & { identifier?: string }) | null = intersect.object?.parent as (THREE.Object3D & { identifier?: string }) | null;
    while (parent && !parent.identifier) {
      parent = parent.parent as (THREE.Object3D & { identifier?: string }) | null;
    }
    return parent || null;
  }

  getHoveredBox() {
    const boxes = this.viewer.scene.volumes.filter((m) => m.visible && m.finished);
    const hoveredBoxes = this.raycaster
      .intersectObjects(boxes, false)
      .map((intersect) => intersect.object as THREE.Object3D & { getVolume(): number })
      .sort((a, b) => a.getVolume() - b.getVolume());
    return hoveredBoxes.at(0);
  }

  getTouchedMeasure() {
    const touchables = this.viewer.scene.measurements
      .filter((m) => m.visible)
      .filter((m) => !m.isCADMeasure)
      .flatMap((m) => {
        const measureTouchables: THREE.Object3D[] = [...m.spheres];
        if (m.type !== 'POINT') {
          measureTouchables.push(m.backgroundLineTouch);
        }
        return measureTouchables;
      });

    const intersects = this.raycaster.intersectObjects(touchables);

    return this.getParentMeasure(intersects.at(0)?.object);
  }

  getParentMeasure(object: THREE.Object3D | undefined) {
    let parent: THREE.Object3D | null | undefined = object?.parent;
    while (parent && !(parent.type === MeasureTools.AREA || parent.type === MeasureTools.DISTANCE || parent.type === MeasureTools.POINT)) {
      parent = parent.parent;
    }
    return parent;
  }

  isHovered(object: THREE.Object3D) {
    const intersect = this.raycaster.intersectObject(object);
    return intersect.length;
  }

  getPointOnLineClosestToCursor(lineOrigin: THREE.Vector3, lineDirection: THREE.Vector3) {
    if (lineDirection.length() === 0) return;
    const ray = this.raycaster.ray;
    const w0 = ray.origin.clone().sub(lineOrigin);
    const a = lineDirection.dot(lineDirection);
    const b = lineDirection.dot(ray.direction);
    const c = ray.direction.dot(ray.direction);
    const dDotW0 = lineDirection.dot(w0);
    const rayDirectionDotW0 = ray.direction.dot(w0);

    const denom = a * c - b * b;
    const s = (b * rayDirectionDotW0 - c * dDotW0) / denom;
    const target = lineOrigin.clone().sub(lineDirection.clone().multiplyScalar(s));

    return target;
  }

  setScene(scene: InputHandlerViewer['scene']) {
    this.scene = scene;
  }

  update(_delta: number) {
    // noop
  }

  setMouse(x: number, y: number) {
    const camera = this.viewer.scene.getActiveCamera();

    this.mouse.set(x, y);

    this.nmouse.x = (x / this.viewer.renderer.domElement.width) * 2 - 1;
    this.nmouse.y = -(y / this.viewer.renderer.domElement.height) * 2 + 1;

    this.raycaster.setFromCamera(this.nmouse, camera);

    // Because potree for some reasons sets the camera's near to a negative value, we have to adjust the origin of the ray as well.
    if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
      const ray = this.raycaster.ray;
      ray.origin.addScaledVector(ray.direction, (camera as THREE.OrthographicCamera).near);
    }
  }
}
